import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TipoLogoEmpresa } from '../../enums/usuario/tipo-logo-empresa.enum';
import { AuthService } from '../apis/usuario/auth.service';
import { EmpresaService } from '../apis/usuario/empresa.service';
import { EtiquetaCampoRequest } from '../requests/etiqueta/etiqueta-campo.request';
import { UsuarioLoginResponse } from '../responses/usuario/usuario-login.response';
import { EmpresaStruct } from '../structs/usuario/empresa.struct';

interface EtiquetaCampoLayoutResumo {
  id?: string;
  chave?: string;
}

type EmpresaDadosParciais = Partial<EmpresaStruct> & Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class EmpresaEtiquetaDadosService {
  private readonly empresaChaves = new Set([
    'empresa.nome',
    'empresa.logo',
    'empresa.cnpj',
    'empresa.endereco_completo',
    'empresa.cep',
    'empresa.logradouro',
    'empresa.numero',
    'empresa.complemento',
    'empresa.bairro',
    'empresa.cidade',
    'empresa.uf',
  ]);

  private empresaCompletaPromise: Promise<EmpresaStruct | null> | null = null;

  constructor(
    private authService: AuthService,
    private empresaService: EmpresaService
  ) {}

  public async enriquecerDadosEtiquetas(
    dadosEtiquetas: Record<string, string>[] | undefined,
    campos: EtiquetaCampoRequest[] | undefined
  ): Promise<Record<string, string>[]> {
    const dados = dadosEtiquetas || [];
    const camposEmpresa = this.getCamposEmpresa(campos);

    if (dados.length === 0 || camposEmpresa.length === 0) {
      return dados;
    }

    const valoresEmpresa = await this.getValoresEmpresa();

    for (const registro of dados) {
      for (const campo of camposEmpresa) {
        if (!campo.id || !campo.chave) continue;
        registro[campo.id] = valoresEmpresa[campo.chave] ?? '';
      }
    }

    return dados;
  }

  private getCamposEmpresa(campos: EtiquetaCampoRequest[] | undefined): EtiquetaCampoLayoutResumo[] {
    return (campos || [])
      .map(campo => this.parseLayout(campo.layoutJson))
      .filter((layout): layout is EtiquetaCampoLayoutResumo =>
        !!layout?.id && !!layout?.chave && this.empresaChaves.has(layout.chave)
      );
  }

  private parseLayout(layoutJson?: string): EtiquetaCampoLayoutResumo | null {
    if (!layoutJson) return null;

    try {
      return JSON.parse(layoutJson) as EtiquetaCampoLayoutResumo;
    } catch {
      return null;
    }
  }

  public async getValoresEmpresa(): Promise<Record<string, string>> {
    const userInfo = this.authService.getUserInfos();
    const empresaSessao = this.getEmpresaSessao(userInfo);
    const precisaCompletar = this.precisaCompletarEmpresa(empresaSessao);
    const empresaApi = precisaCompletar ? await this.getEmpresaCompleta(userInfo.idEmpresa) : null;
    const empresa = this.mergeEmpresa(empresaSessao, empresaApi, userInfo);

    return {
      'empresa.nome': this.valor(empresa.nomeEmpresa || userInfo.nomeEmpresa),
      'empresa.logo': this.getLogoUrl(empresa),
      'empresa.cnpj': this.formatarCnpj(empresa.cnpj || userInfo.cnpj),
      'empresa.endereco_completo': this.formatarEnderecoCompleto(empresa),
      'empresa.cep': this.formatarCep(empresa.cep),
      'empresa.logradouro': this.valor(empresa.logradouro),
      'empresa.numero': this.valor(empresa.numero),
      'empresa.complemento': this.valor(empresa.complemento),
      'empresa.bairro': this.valor(empresa.bairro),
      'empresa.cidade': this.valor(empresa.municipio || empresa['cidade']),
      'empresa.uf': this.valor(empresa.uf),
    };
  }

  private getEmpresaSessao(userInfo: UsuarioLoginResponse): EmpresaDadosParciais {
    const listas = [
      userInfo.listaEmpresa || [],
      userInfo.listaEmpresasDoGrupo || [],
    ];

    for (const lista of listas) {
      const empresas = lista as unknown as EmpresaDadosParciais[];
      const encontrada = empresas.find(empresa => empresa.idEmpresa === userInfo.idEmpresa);
      if (encontrada) {
        return { ...encontrada };
      }
    }

    return {
      idEmpresa: userInfo.idEmpresa,
      nomeEmpresa: userInfo.nomeEmpresa,
      cnpj: userInfo.cnpj,
    };
  }

  private precisaCompletarEmpresa(empresa: EmpresaDadosParciais): boolean {
    return !empresa.municipio
      || !empresa.uf
      || !empresa.logos?.some((logo: any) => !!logo?.urlLogo);
  }

  private async getEmpresaCompleta(idEmpresa: number): Promise<EmpresaStruct | null> {
    if (!idEmpresa) {
      return null;
    }

    if (!this.empresaCompletaPromise) {
      this.empresaCompletaPromise = firstValueFrom(this.empresaService.obterDadosPorId(idEmpresa))
        .then(response => response?.isError ? null : response?.empresa ?? null)
        .catch(() => null);
    }

    return this.empresaCompletaPromise;
  }

  private mergeEmpresa(
    empresaSessao: EmpresaDadosParciais,
    empresaApi: EmpresaStruct | null,
    userInfo: UsuarioLoginResponse
  ): EmpresaDadosParciais {
    return {
      idEmpresa: userInfo.idEmpresa,
      nomeEmpresa: userInfo.nomeEmpresa,
      cnpj: userInfo.cnpj,
      ...empresaSessao,
      ...(empresaApi || {}),
    };
  }

  private formatarEnderecoCompleto(empresa: EmpresaDadosParciais): string {
    const logradouroNumero = [empresa.logradouro, empresa.numero]
      .map(valor => this.valor(valor))
      .filter(Boolean)
      .join(', ');

    const bairro = this.valor(empresa.bairro);
    const cidadeUf = [this.valor(empresa.municipio || empresa['cidade']), this.valor(empresa.uf)]
      .filter(Boolean)
      .join('/');
    const cep = this.formatarCep(empresa.cep);

    const partes = [
      logradouroNumero,
      bairro,
      cidadeUf,
      cep ? `CEP ${cep}` : '',
    ].filter(Boolean);

    if (partes.length === 0) {
      return '';
    }

    if (partes.length === 1) {
      return partes[0];
    }

    const [primeira, ...demais] = partes;
    return `${primeira} - ${demais.join(', ')}`;
  }

  private getLogoUrl(empresa: EmpresaDadosParciais): string {
    const logos = empresa.logos || [];
    const principal = logos.find((logo: any) => logo?.tipo === TipoLogoEmpresa.Principal && logo?.urlLogo);
    const primeira = logos.find((logo: any) => !!logo?.urlLogo);

    return this.valor((principal || primeira)?.urlLogo);
  }

  private formatarCnpj(cnpj?: string | null): string {
    const valor = this.valor(cnpj).replace(/\D/g, '');
    if (valor.length !== 14) {
      return this.valor(cnpj);
    }

    return `${valor.substring(0, 2)}.${valor.substring(2, 5)}.${valor.substring(5, 8)}/${valor.substring(8, 12)}-${valor.substring(12, 14)}`;
  }

  private formatarCep(cep?: string | null): string {
    const valor = this.valor(cep).replace(/\D/g, '');
    if (valor.length !== 8) {
      return this.valor(cep);
    }

    return `${valor.substring(0, 5)}-${valor.substring(5, 8)}`;
  }

  private valor(value: unknown): string {
    return String(value ?? '').trim();
  }
}
